import {
  applyDoubleFreeTransition,
  boundaryCellForBlock,
  computeOverflowRange,
  dangerLabelForValue,
  isDanglingPointer,
  nextDanger,
} from '../../lib/labs/memory-behavior';
import { rangeToRowSegments } from '../../lib/labs/memory-playground';

type RegionName = 'stack' | 'heap' | 'free';

type Block = {
  id: string;
  start: number;
  size: number;
  allocated: boolean;
  generation: number;
  color: string;
  label: string;
  region: 'heap' | 'stack';
  originalId?: string;
};

type Pointer = {
  id: string;
  label: string;
  fromAddress: number;
  toAddress: number;
  originalBlockId: string;
  originalGeneration: number;
};

type Range = {
  start: number;
  end: number;
};

type StoryStep = {
  id: string;
  title: string;
  program: string;
  explain: string;
  why: string;
  try: string;
};

const GRID_WIDTH = 32;
const GRID_HEIGHT = 16;
const MEMORY_SIZE = GRID_WIDTH * GRID_HEIGHT;

const STACK_RANGE: Range = { start: 0, end: 127 };
const HEAP_RANGE: Range = { start: 160, end: 415 };

const STORY_STEPS: StoryStep[] = [
  {
    id: 'safe',
    title: 'Step 1 of 5',
    program: 'Write 12 bytes into a 16-byte buffer. All writes stay in bounds.',
    explain: 'The write stayed inside the buffer. Only those bytes changed.',
    why: 'Why it matters: staying in bounds preserves nearby data.',
    try: 'Try this: click a few bytes inside the buffer to flip their color.',
  },
  {
    id: 'overflow',
    title: 'Step 2 of 5',
    program: 'Write 20 bytes into a 16-byte buffer. Four bytes overflow.',
    explain: 'The overflow crossed the boundary and altered nearby bytes.',
    why: 'Why it matters: overflow can damage adjacent data structures.',
    try: 'Try this: click the highlighted boundary cells and watch the flash.',
  },
  {
    id: 'free-reuse',
    title: 'Step 3 of 5',
    program: 'Free a block, then allocate again to reuse the space.',
    explain: 'The allocator reused a freed block for a new object.',
    why: 'Why it matters: old pointers may now reference someone else’s data.',
    try: 'Try this: note how the block label changes after reuse.',
  },
  {
    id: 'uaf',
    title: 'Step 4 of 5',
    program: 'Use a pointer after its block is freed and reused.',
    explain: 'The pointer still writes to the old address, corrupting new data.',
    why: 'Why it matters: use-after-free bugs are subtle and dangerous.',
    try: 'Try this: watch the pointer arrow turn red.',
  },
  {
    id: 'double-free',
    title: 'Step 5 of 5',
    program: 'Free the same block twice to corrupt allocator state.',
    explain: 'The free list now contains duplicate entries for the same block.',
    why: 'Why it matters: allocator confusion can lead to memory corruption.',
    try: 'Try this: hit Reset to see the allocator recover.',
  },
];

const ACTION_COPY = {
  safe: {
    program: 'Write 12 bytes into a 16-byte buffer. All writes stay in bounds.',
    explain: 'The write stayed inside the buffer. Only those bytes changed.',
    why: 'Why it matters: staying in bounds preserves nearby data.',
    try: 'Try this: click inside the block to flip bytes.',
  },
  overflow: {
    program: 'Write 20 bytes into a 16-byte buffer. Four bytes overflow.',
    explain: 'The overflow crossed the boundary and altered nearby bytes.',
    why: 'Why it matters: overflow can damage adjacent data structures.',
    try: 'Try this: watch the boundary cells pulse red.',
  },
  free: {
    program: 'Free a heap block so the allocator can reuse it.',
    explain: 'The block is now free and back on the free list.',
    why: 'Why it matters: freed memory can be reused immediately.',
    try: 'Try this: allocate again to see reuse.',
  },
  uaf: {
    program: 'Use a pointer after its block was freed.',
    explain:
      'The dangling pointer wrote into memory that may now belong to someone else.',
    why: 'Why it matters: use-after-free can corrupt new objects.',
    try: 'Try this: watch the pointer arrow turn red.',
  },
  doubleFree: {
    program: 'Free the same block twice.',
    explain: 'The allocator’s free list now has a duplicate entry.',
    why: 'Why it matters: double-free corrupts allocator bookkeeping.',
    try: 'Try this: reset to restore a clean state.',
  },
  reset: {
    program: 'Reset the simulation to a safe default.',
    explain: 'Memory is clean and the safe write scenario is loaded.',
    why: 'Why it matters: a clean state makes it easy to replay scenarios.',
    try: 'Try this: advance Story Mode to re-run the steps.',
  },
};

type CodeSnippet = {
  description: string;
  code: string;
  verdict: string;
  isDanger: boolean;
};

const CODE_SNIPPETS: Record<string, CodeSnippet> = {
  safe: {
    description: 'Safe write: data fits within buffer bounds.',
    code: `<span class="code-type">char</span> buffer[<span class="code-num">16</span>];
<span class="code-type">char</span> *data = <span class="code-str">"Hello World!"</span>; <span class="code-comment">// 12 bytes</span>

<span class="code-fn">memcpy</span>(buffer, data, <span class="code-num">12</span>);
<span class="code-comment">// Safe: 12 &lt;= 16</span>`,
    verdict: 'Write stays within allocated bounds.',
    isDanger: false,
  },
  overflow: {
    description: 'Buffer overflow: write exceeds buffer size.',
    code: `<span class="code-type">char</span> buffer[<span class="code-num">16</span>];
<span class="code-type">char</span> *data = <span class="code-str">"AAAAAAAAAAAAAAAABBBB"</span>; <span class="code-comment">// 20 bytes</span>

<span class="code-highlight"><span class="code-fn">memcpy</span>(buffer, data, <span class="code-danger">20</span>);</span>
<span class="code-comment">// BUG: 20 &gt; 16, overwrites 4 adjacent bytes</span>`,
    verdict: 'Overflow corrupts adjacent memory (4 bytes).',
    isDanger: true,
  },
  overflowStack: {
    description: 'Stack buffer overflow: overwrites return address.',
    code: `<span class="code-type">void</span> <span class="code-fn">vulnerable</span>() {
  <span class="code-type">char</span> buffer[<span class="code-num">16</span>];        <span class="code-comment">// on stack</span>
  <span class="code-type">char</span> *input = <span class="code-fn">get_input</span>(); <span class="code-comment">// 20+ bytes</span>

  <span class="code-highlight"><span class="code-fn">strcpy</span>(buffer, input);</span>  <span class="code-comment">// no bounds check!</span>
}
<span class="code-comment">// Return address lives just past buffer</span>
<span class="code-comment">// Overflow overwrites RIP → attacker controls execution</span>`,
    verdict: 'Return address overwritten → arbitrary code execution.',
    isDanger: true,
  },
  free: {
    description: 'Free: returning memory to the allocator.',
    code: `<span class="code-type">char</span> *ptr = <span class="code-fn">malloc</span>(<span class="code-num">24</span>);
<span class="code-fn">strcpy</span>(ptr, <span class="code-str">"sensitive data"</span>);

<span class="code-comment">// Done with buffer</span>
<span class="code-fn">free</span>(ptr);
<span class="code-comment">// Block returns to free list</span>
<span class="code-comment">// ptr is now a dangling pointer!</span>`,
    verdict: 'Memory freed. Pointer is now dangling.',
    isDanger: false,
  },
  freeReuse: {
    description: 'Heap reuse: allocator recycles freed block.',
    code: `<span class="code-type">char</span> *old = <span class="code-fn">malloc</span>(<span class="code-num">24</span>);
<span class="code-fn">free</span>(old);              <span class="code-comment">// old is dangling</span>

<span class="code-type">char</span> *new = <span class="code-fn">malloc</span>(<span class="code-num">24</span>); <span class="code-comment">// reuses same block!</span>
<span class="code-fn">strcpy</span>(new, <span class="code-str">"new object"</span>);

<span class="code-comment">// old and new point to same address</span>
<span class="code-comment">// old can corrupt new's data</span>`,
    verdict: 'Freed block reused. Old pointer still valid address.',
    isDanger: false,
  },
  uaf: {
    description: 'Use-after-free: writing through dangling pointer.',
    code: `<span class="code-type">char</span> *victim = <span class="code-fn">malloc</span>(<span class="code-num">16</span>);
<span class="code-fn">free</span>(victim);

<span class="code-type">char</span> *new_obj = <span class="code-fn">malloc</span>(<span class="code-num">16</span>); <span class="code-comment">// reuses block</span>
<span class="code-fn">strcpy</span>(new_obj, <span class="code-str">"important"</span>);

<span class="code-comment">// Attacker still has victim pointer...</span>
<span class="code-highlight"><span class="code-fn">strcpy</span>(victim, <span class="code-danger">"EVIL"</span>);</span> <span class="code-comment">// corrupts new_obj!</span>`,
    verdict: 'Dangling pointer corrupts reallocated object.',
    isDanger: true,
  },
  doubleFree: {
    description: 'Double-free: freeing the same block twice.',
    code: `<span class="code-type">char</span> *ptr = <span class="code-fn">malloc</span>(<span class="code-num">16</span>);
<span class="code-fn">free</span>(ptr);

<span class="code-comment">// ... later, bug causes second free ...</span>
<span class="code-highlight"><span class="code-fn">free</span>(ptr);</span> <span class="code-comment">// <span class="code-danger">DOUBLE FREE!</span></span>

<span class="code-comment">// Free list now corrupted:</span>
<span class="code-comment">// block → block → ... (cycle)</span>
<span class="code-comment">// Next two mallocs return same address</span>`,
    verdict: 'Allocator metadata corrupted. Exploitable.',
    isDanger: true,
  },
  reset: {
    description: 'Safe write: data fits within buffer bounds.',
    code: `<span class="code-type">char</span> buffer[<span class="code-num">16</span>];
<span class="code-type">char</span> *data = <span class="code-str">"Hello World!"</span>; <span class="code-comment">// 12 bytes</span>

<span class="code-fn">memcpy</span>(buffer, data, <span class="code-num">12</span>);
<span class="code-comment">// Safe: 12 &lt;= 16</span>`,
    verdict: 'Write stays within allocated bounds.',
    isDanger: false,
  },
};

const REGION_BASES: Record<RegionName, [number, number, number]> = {
  stack: [80, 130, 255],
  heap: [70, 200, 160],
  free: [96, 96, 96],
};

const blocksPalette = ['#22c55e', '#38bdf8', '#f97316', '#a855f7', '#eab308'];

const root = document.querySelector('[data-memory-playground]') as HTMLElement | null;

const initMemoryPlayground = (rootEl: HTMLElement) => {
  const getEl = <T extends Element>(selector: string) =>
    (rootEl.querySelector(selector) ?? document.querySelector(selector)) as T | null;

  const canvas = getEl<HTMLCanvasElement>('#memory-canvas');
  const codeView = getEl<HTMLElement>('#code-view code');
  const codeDescription = getEl<HTMLElement>('#code-description');
  const codeVerdict = getEl<HTMLElement>('#code-verdict');
  const codeVerdictContainer = codeVerdict?.parentElement as HTMLElement | null;
  const explainTitle = getEl<HTMLElement>('#explain-title');
  const explainBody = getEl<HTMLElement>('#explain-body');
  const explainWhy = getEl<HTMLElement>('#explain-why');
  const explainTry = getEl<HTMLElement>('#explain-try');
  const dangerBar = getEl<HTMLElement>('#danger-bar');
  const dangerValue = getEl<HTMLElement>('#danger-value');
  const dangerLabel = getEl<HTMLElement>('#danger-label');
  const storyTitle = getEl<HTMLElement>('#story-step-title');
  const storyText = getEl<HTMLElement>('#story-step-text');
  const storyTry = getEl<HTMLElement>('#story-step-try');
  const storyProgress = getEl<HTMLElement>('#story-progress');
  const storySummary = getEl<HTMLElement>('#story-summary');

  const btnSafe = getEl<HTMLButtonElement>('#btn-safe');
  const btnOverflow = getEl<HTMLButtonElement>('#btn-overflow');
  const btnFree = getEl<HTMLButtonElement>('#btn-free');
  const btnUaf = getEl<HTMLButtonElement>('#btn-uaf');
  const btnDoubleFree = getEl<HTMLButtonElement>('#btn-double-free');
  const btnReset = getEl<HTMLButtonElement>('#btn-reset');
  const btnNext = getEl<HTMLButtonElement>('#story-next');
  const btnBack = getEl<HTMLButtonElement>('#story-back');
  const freePlayToggle = getEl<HTMLInputElement>('#free-play-toggle');
  const advancedToggle = getEl<HTMLInputElement>('#advanced-toggle');

  if (
    !canvas ||
    !codeView ||
    !codeDescription ||
    !codeVerdict ||
    !explainTitle ||
    !explainBody ||
    !explainWhy ||
    !explainTry ||
    !dangerBar ||
    !dangerValue ||
    !dangerLabel ||
    !storyTitle ||
    !storyText ||
    !storyTry ||
    !storyProgress ||
    !storySummary ||
    !btnSafe ||
    !btnOverflow ||
    !btnFree ||
    !btnUaf ||
    !btnDoubleFree ||
    !btnReset ||
    !btnNext ||
    !btnBack ||
    !freePlayToggle ||
    !advancedToggle
  ) {
    console.error('Memory playground: missing required elements.');
    return;
  }

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas context not available');
  }

  const memory = new Uint8Array(MEMORY_SIZE);
  let blocks: Block[] = [];
  let pointers: Pointer[] = [];
  let freeList: Range[] = [];
  let heapCursor = HEAP_RANGE.start;
  let corruptedCells = new Set<number>();
  let boundaryCells = new Set<number>();
  let danger = 0;
  let storyIndex = 0;
  let freePlay = true;
  let advancedMode = false;
  let returnAddressRange: Range | null = null;
  let shakeUntil = 0;
  let cursor = { x: 0, y: 0 };
  let cursorVisible = false;

  const resizeObserver = new ResizeObserver(() => resizeCanvas());
  resizeObserver.observe(canvas);

  function resetState() {
    memory.fill(0);
    blocks = [];
    pointers = [];
    freeList = [];
    heapCursor = HEAP_RANGE.start;
    corruptedCells = new Set<number>();
    boundaryCells = new Set<number>();
    danger = 0;
    shakeUntil = 0;
    cursorVisible = false;

    const bufferA = allocateBlock(16, 'Buffer A');
    const bufferB = allocateBlock(24, 'Buffer B');
    allocateBlock(16, 'Buffer C');

    pointers = [
      {
        id: 'ptrA',
        label: 'ptrA',
        fromAddress: 32,
        toAddress: bufferA.start,
        originalBlockId: bufferA.id,
        originalGeneration: bufferA.generation,
      },
      {
        id: 'ptrB',
        label: 'ptrB',
        fromAddress: 40,
        toAddress: bufferB.start,
        originalBlockId: bufferB.id,
        originalGeneration: bufferB.generation,
      },
    ];

    if (advancedMode) {
      const stackBuffer: Block = {
        id: 'stack-buf',
        start: 80,
        size: 16,
        allocated: true,
        generation: 0,
        color: '#60a5fa',
        label: 'Stack buffer',
        region: 'stack',
      };
      blocks.push(stackBuffer);
      returnAddressRange = { start: 96, end: 103 };
    } else {
      returnAddressRange = null;
    }

    updateDangerMeter();
  }

  function allocateBlock(size: number, label: string): Block {
    let start = heapCursor;
    let reused = false;
    let reuseIndex = -1;

    for (let i = 0; i < freeList.length; i += 1) {
      const free = freeList[i];
      if (free.end - free.start + 1 >= size) {
        start = free.start;
        reuseIndex = i;
        reused = true;
        break;
      }
    }

    if (reused && reuseIndex >= 0) {
      const free = freeList.splice(reuseIndex, 1)[0];
      const remaining = free.end - free.start + 1 - size;
      if (remaining > 0) {
        freeList.push({ start: free.start + size, end: free.end });
      }
    } else {
      heapCursor = Math.min(HEAP_RANGE.end + 1, heapCursor + size);
    }

    const existing = blocks.find(
      (block) => block.start === start && block.region === 'heap'
    );
    if (existing) {
      existing.size = size;
      existing.allocated = true;
      existing.generation += 1;
      existing.label = label;
      existing.originalId = existing.originalId ?? existing.id;
      return existing;
    }

    const block: Block = {
      id: `block-${blocks.length + 1}`,
      start,
      size,
      allocated: true,
      generation: 0,
      color: blocksPalette[blocks.length % blocksPalette.length],
      label,
      region: 'heap',
    };
    blocks.push(block);
    return block;
  }

  function freeBlock(blockId: string) {
    const block = blocks.find((item) => item.id === blockId);
    if (!block || !block.allocated) return;
    block.allocated = false;
    freeList.push({ start: block.start, end: block.start + block.size - 1 });
  }

  function doubleFree(blockId: string) {
    const block = blocks.find((item) => item.id === blockId);
    if (!block) return;
    const transition = applyDoubleFreeTransition(block, freeList);
    block.allocated = transition.nextAllocated;
    freeList = transition.nextFreeList;
    if (transition.corruptionRange) {
      triggerCorruption(
        transition.corruptionRange.start,
        transition.corruptionRange.end,
        40
      );
    }
  }

  function safeWrite(blockId: string, length: number) {
    const block = blocks.find((item) => item.id === blockId);
    if (!block) return;
    writeBytes(block.start, Math.min(length, block.size));
    setBoundaryCells(block);
  }

  function overflowWrite(blockId: string, length: number) {
    const block = blocks.find((item) => item.id === blockId);
    if (!block) return;
    const safeLength = Math.min(length, block.size);
    writeBytes(block.start, safeLength);
    const overflowRange = computeOverflowRange(
      block.start,
      block.size,
      length,
      MEMORY_SIZE
    );
    if (overflowRange) {
      writeBytes(overflowRange.start, overflowRange.end - overflowRange.start + 1);
      triggerCorruption(overflowRange.start, overflowRange.end, 30);
    }
    setBoundaryCells(block);
    if (returnAddressRange) {
      for (let i = returnAddressRange.start; i <= returnAddressRange.end; i += 1) {
        if (corruptedCells.has(i)) {
          triggerCorruption(returnAddressRange.start, returnAddressRange.end, 20);
          break;
        }
      }
    }
  }

  function useAfterFree(pointerId: string) {
    const pointer = pointers.find((item) => item.id === pointerId);
    if (!pointer) return;
    writeBytes(pointer.toAddress, 8);
    triggerCorruption(pointer.toAddress, pointer.toAddress + 7, 25);
  }

  function writeBytes(start: number, length: number) {
    for (let i = 0; i < length; i += 1) {
      const addr = start + i;
      if (addr < 0 || addr >= MEMORY_SIZE) continue;
      memory[addr] = (memory[addr] + 64 + i * 13) % 256;
    }
  }

  function triggerCorruption(start: number, end: number, amount: number) {
    for (let i = start; i <= end; i += 1) {
      if (i >= 0 && i < MEMORY_SIZE) {
        corruptedCells.add(i);
      }
    }
    danger = nextDanger(danger, amount);
    shakeUntil = performance.now() + 600;
    updateDangerMeter();
  }

  function setBoundaryCells(block: Block) {
    boundaryCells = new Set<number>();
    const boundary = boundaryCellForBlock(block.start, block.size, MEMORY_SIZE);
    if (boundary !== null) {
      boundaryCells.add(boundary);
    }
  }

  function getRegion(addr: number): RegionName {
    if (addr >= STACK_RANGE.start && addr <= STACK_RANGE.end) {
      return 'stack';
    }
    if (addr >= HEAP_RANGE.start && addr <= HEAP_RANGE.end) {
      return 'heap';
    }
    return 'free';
  }

  function getBlockAt(addr: number): Block | undefined {
    return blocks.find(
      (block) =>
        addr >= block.start && addr < block.start + block.size && block.region === 'heap'
    );
  }

  function isPointerDangling(pointer: Pointer): boolean {
    const block = getBlockAt(pointer.toAddress);
    return isDanglingPointer(pointer, block ?? null);
  }

  function updateDangerMeter() {
    dangerBar.style.width = `${danger}%`;
    dangerValue.textContent = `${danger} / 100`;
    const label = dangerLabelForValue(danger);
    dangerLabel.textContent = `Danger: ${label}`;
  }

  function updateExplanation(copy: {
    program: string;
    explain: string;
    why: string;
    try: string;
  }) {
    explainBody.textContent = copy.explain;
    explainWhy.textContent = copy.why;
    explainTry.textContent = copy.try;
  }

  function updateCodeView(snippetKey: string) {
    const snippet = CODE_SNIPPETS[snippetKey];
    if (!snippet || !codeView) return;

    codeView.innerHTML = snippet.code;
    if (codeDescription) codeDescription.textContent = snippet.description;
    if (codeVerdict) codeVerdict.textContent = snippet.verdict;

    if (codeVerdictContainer) {
      codeVerdictContainer.classList.remove(
        'border-emerald-500/20',
        'bg-emerald-500/5',
        'border-rose-500/20',
        'bg-rose-500/5'
      );
      if (snippet.isDanger) {
        codeVerdictContainer.classList.add('border-rose-500/20', 'bg-rose-500/5');
        codeVerdictContainer.querySelector('span')!.innerHTML = '&#10007;';
        codeVerdictContainer.querySelector('span')!.classList.remove('text-emerald-500');
        codeVerdictContainer.querySelector('span')!.classList.add('text-rose-500');
      } else {
        codeVerdictContainer.classList.add('border-emerald-500/20', 'bg-emerald-500/5');
        codeVerdictContainer.querySelector('span')!.innerHTML = '&#10003;';
        codeVerdictContainer.querySelector('span')!.classList.remove('text-rose-500');
        codeVerdictContainer.querySelector('span')!.classList.add('text-emerald-500');
      }
    }
  }

  function applyAdvancedNote(isOverflow: boolean) {
    if (!advancedMode || !isOverflow) return;
    explainBody.textContent = `${explainBody.textContent} The overflow reaches the return address.`;
    explainWhy.textContent =
      'Why it matters: overwriting return addresses can change control flow in real programs.';
    updateCodeView('overflowStack');
  }

  function primaryBufferId() {
    return advancedMode ? 'stack-buf' : 'block-1';
  }

  function applyStoryStep(index: number) {
    storyIndex = Math.max(0, Math.min(STORY_STEPS.length - 1, index));
    const step = STORY_STEPS[storyIndex];
    storyTitle.textContent = step.title;
    storyText.textContent = step.program;
    storyTry.textContent = step.try;
    storyProgress.style.width = `${((storyIndex + 1) / STORY_STEPS.length) * 100}%`;
    storySummary.classList.toggle('hidden', storyIndex !== STORY_STEPS.length - 1);
    btnBack.disabled = storyIndex === 0;
    btnNext.disabled = storyIndex === STORY_STEPS.length - 1;

    resetState();
    switch (step.id) {
      case 'safe':
        safeWrite(primaryBufferId(), 12);
        updateExplanation(stepToCopy(step));
        updateCodeView('safe');
        break;
      case 'overflow':
        overflowWrite(primaryBufferId(), 20);
        updateExplanation(stepToCopy(step));
        updateCodeView('overflow');
        applyAdvancedNote(true);
        break;
      case 'free-reuse':
        freeBlock('block-2');
        allocateBlock(24, 'Reused block');
        updateExplanation(stepToCopy(step));
        updateCodeView('freeReuse');
        break;
      case 'uaf':
        freeBlock('block-1');
        allocateBlock(16, 'Reused block');
        useAfterFree('ptrA');
        updateExplanation(stepToCopy(step));
        updateCodeView('uaf');
        break;
      case 'double-free':
        freeBlock('block-3');
        doubleFree('block-3');
        updateExplanation(stepToCopy(step));
        updateCodeView('doubleFree');
        break;
      default:
        updateExplanation(stepToCopy(step));
        updateCodeView('safe');
    }

    updateDangerMeter();
    render();
  }

  function stepToCopy(step: StoryStep) {
    return {
      program: step.program,
      explain: step.explain,
      why: step.why,
      try: step.try,
    };
  }

  function updateActionAvailability() {
    const disabled = !freePlay;
    [btnSafe, btnOverflow, btnFree, btnUaf, btnDoubleFree].forEach((btn) => {
      btn.disabled = disabled;
      btn.classList.toggle('opacity-40', disabled);
      btn.classList.toggle('cursor-not-allowed', disabled);
      btn.setAttribute('aria-disabled', disabled ? 'true' : 'false');
    });
  }

  function handleAction(action: keyof typeof ACTION_COPY) {
    resetState();
    let codeKey = action as string;
    switch (action) {
      case 'safe':
        safeWrite(primaryBufferId(), 12);
        break;
      case 'overflow':
        overflowWrite(primaryBufferId(), 20);
        break;
      case 'free':
        freeBlock('block-2');
        break;
      case 'uaf':
        freeBlock('block-1');
        allocateBlock(16, 'Reused block');
        useAfterFree('ptrA');
        break;
      case 'doubleFree':
        freeBlock('block-3');
        doubleFree('block-3');
        break;
      case 'reset':
        safeWrite(primaryBufferId(), 12);
        codeKey = 'safe';
        break;
      default:
        break;
    }
    updateExplanation(ACTION_COPY[action]);
    updateCodeView(codeKey);
    updateDangerMeter();
    if (action === 'overflow') {
      applyAdvancedNote(true);
    }
    render();
  }

  function resizeCanvas() {
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.max(1, Math.floor(rect.width * dpr));
    canvas.height = Math.max(1, Math.floor(rect.height * dpr));
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    render();
  }

  function addressToCell(addr: number) {
    return { x: addr % GRID_WIDTH, y: Math.floor(addr / GRID_WIDTH) };
  }

  function cellToAddress(x: number, y: number) {
    return y * GRID_WIDTH + x;
  }

  function cellRect(x: number, y: number, cellSize: number) {
    return {
      x: x * cellSize,
      y: y * cellSize,
      w: cellSize,
      h: cellSize,
    };
  }

  function drawGrid(cellSize: number) {
    const now = performance.now();
    for (let addr = 0; addr < MEMORY_SIZE; addr += 1) {
      const { x, y } = addressToCell(addr);
      const value = memory[addr];
      const region = getRegion(addr);
      const base = REGION_BASES[region];
      const intensity = 0.25 + (value / 255) * 0.6;
      const color = `rgb(${Math.floor(base[0] * intensity)}, ${Math.floor(
        base[1] * intensity
      )}, ${Math.floor(base[2] * intensity)})`;

      const rect = cellRect(x, y, cellSize);
      ctx.fillStyle = color;
      ctx.fillRect(rect.x, rect.y, rect.w, rect.h);

      ctx.strokeStyle = 'rgba(255,255,255,0.04)';
      ctx.strokeRect(rect.x, rect.y, rect.w, rect.h);

      if (boundaryCells.has(addr)) {
        ctx.strokeStyle = 'rgba(251,191,36,0.8)';
        ctx.lineWidth = 2;
        ctx.strokeRect(rect.x + 1, rect.y + 1, rect.w - 2, rect.h - 2);
        ctx.lineWidth = 1;
      }

      if (corruptedCells.has(addr)) {
        const pulse = Math.sin(now / 120) * 0.5 + 0.5;
        ctx.strokeStyle = `rgba(244,63,94,${0.4 + pulse * 0.6})`;
        ctx.lineWidth = 2;
        ctx.strokeRect(rect.x + 1, rect.y + 1, rect.w - 2, rect.h - 2);
        ctx.lineWidth = 1;
      }
    }
  }

  function drawRegions(cellSize: number) {
    ctx.font = '12px sans-serif';
    ctx.fillStyle = 'rgba(148,163,184,0.9)';
    const stackLabel = addressToCell(STACK_RANGE.start);
    ctx.fillText('Stack', stackLabel.x * cellSize + 6, stackLabel.y * cellSize + 14);
    const heapLabel = addressToCell(HEAP_RANGE.start);
    ctx.fillText('Heap', heapLabel.x * cellSize + 6, heapLabel.y * cellSize + 14);
  }

  function drawBlocks(cellSize: number) {
    const now = performance.now();
    const shake = now < shakeUntil ? Math.sin(now / 40) * 2 : 0;
    blocks.forEach((block) => {
      const segments = rangeToRowSegments(
        block.start,
        block.start + block.size - 1,
        GRID_WIDTH,
        MEMORY_SIZE
      );
      if (segments.length === 0) return;

      ctx.save();
      if (!block.allocated) {
        ctx.setLineDash([4, 4]);
        ctx.strokeStyle = 'rgba(148,163,184,0.7)';
      } else {
        ctx.strokeStyle = block.color;
      }
      ctx.lineWidth = 2;
      segments.forEach((segment) => {
        const x = segment.x * cellSize + shake;
        const y = segment.y * cellSize + shake;
        const width = segment.widthCells * cellSize;
        ctx.strokeRect(x + 1, y + 1, width - 2, cellSize - 2);
      });
      ctx.restore();

      const labelSegment = segments[0];
      const labelX = labelSegment.x * cellSize + shake;
      const labelY = labelSegment.y * cellSize + shake;
      ctx.fillStyle = block.allocated ? block.color : 'rgba(148,163,184,0.7)';
      ctx.font = '11px sans-serif';
      ctx.fillText(block.label, labelX + 6, labelY + 14);
    });

    if (returnAddressRange) {
      const segments = rangeToRowSegments(
        returnAddressRange.start,
        returnAddressRange.end,
        GRID_WIDTH,
        MEMORY_SIZE
      );
      if (segments.length === 0) return;

      ctx.strokeStyle = 'rgba(244,63,94,0.8)';
      ctx.lineWidth = 2;
      segments.forEach((segment) => {
        const x = segment.x * cellSize + shake;
        const y = segment.y * cellSize + shake;
        const width = segment.widthCells * cellSize;
        ctx.strokeRect(x + 1, y + 1, width - 2, cellSize - 2);
      });

      const labelSegment = segments[0];
      const labelX = labelSegment.x * cellSize + shake;
      const labelY = labelSegment.y * cellSize + shake;
      ctx.fillStyle = 'rgba(244,63,94,0.8)';
      ctx.font = '11px sans-serif';
      ctx.fillText('Return addr', labelX + 6, labelY + 14);
    }
  }

  function drawPointers(cellSize: number) {
    const now = performance.now();
    const shake = now < shakeUntil ? Math.sin(now / 50) * 2 : 0;
    pointers.forEach((pointer) => {
      const from = addressToCell(pointer.fromAddress);
      const to = addressToCell(pointer.toAddress);
      const startX = from.x * cellSize + cellSize / 2 + shake;
      const startY = from.y * cellSize + cellSize / 2 + shake;
      const endX = to.x * cellSize + cellSize / 2 + shake;
      const endY = to.y * cellSize + cellSize / 2 + shake;
      const dangling = isPointerDangling(pointer);
      ctx.strokeStyle = dangling ? 'rgba(244,63,94,0.9)' : 'rgba(59,130,246,0.8)';
      ctx.lineWidth = 2;
      drawArrow(startX, startY, endX, endY);
      ctx.fillStyle = dangling ? 'rgba(244,63,94,0.9)' : 'rgba(59,130,246,0.8)';
      ctx.font = '10px sans-serif';
      ctx.fillText(pointer.label, startX + 4, startY - 4);
    });
  }

  function drawArrow(x1: number, y1: number, x2: number, y2: number) {
    const headLength = 8;
    const angle = Math.atan2(y2 - y1, x2 - x1);
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(x2, y2);
    ctx.lineTo(
      x2 - headLength * Math.cos(angle - Math.PI / 6),
      y2 - headLength * Math.sin(angle - Math.PI / 6)
    );
    ctx.lineTo(
      x2 - headLength * Math.cos(angle + Math.PI / 6),
      y2 - headLength * Math.sin(angle + Math.PI / 6)
    );
    ctx.lineTo(x2, y2);
    ctx.fill();
  }

  function drawCursor(cellSize: number) {
    if (!cursorVisible) return;
    const rect = cellRect(cursor.x, cursor.y, cellSize);
    ctx.strokeStyle = 'rgba(59,130,246,0.9)';
    ctx.lineWidth = 2;
    ctx.strokeRect(rect.x + 2, rect.y + 2, rect.w - 4, rect.h - 4);
    ctx.lineWidth = 1;
  }

  function render() {
    const rect = canvas.getBoundingClientRect();
    const cellSize = Math.min(rect.width / GRID_WIDTH, rect.height / GRID_HEIGHT);
    ctx.clearRect(0, 0, rect.width, rect.height);
    drawGrid(cellSize);
    drawRegions(cellSize);
    drawBlocks(cellSize);
    drawPointers(cellSize);
    drawCursor(cellSize);
  }

  function animate() {
    render();
    requestAnimationFrame(animate);
  }

  function handleCanvasPointer(clientX: number, clientY: number) {
    const rect = canvas.getBoundingClientRect();
    const cellSize = Math.min(rect.width / GRID_WIDTH, rect.height / GRID_HEIGHT);
    const x = Math.floor((clientX - rect.left) / cellSize);
    const y = Math.floor((clientY - rect.top) / cellSize);
    if (x < 0 || x >= GRID_WIDTH || y < 0 || y >= GRID_HEIGHT) return;
    cursor = { x, y };
    cursorVisible = true;
    const addr = cellToAddress(x, y);
    memory[addr] = memory[addr] === 0 ? 255 : 0;
  }

  function onKeyDown(event: KeyboardEvent) {
    if (event.key === 'ArrowUp') {
      cursor.y = Math.max(0, cursor.y - 1);
      event.preventDefault();
    }
    if (event.key === 'ArrowDown') {
      cursor.y = Math.min(GRID_HEIGHT - 1, cursor.y + 1);
      event.preventDefault();
    }
    if (event.key === 'ArrowLeft') {
      cursor.x = Math.max(0, cursor.x - 1);
      event.preventDefault();
    }
    if (event.key === 'ArrowRight') {
      cursor.x = Math.min(GRID_WIDTH - 1, cursor.x + 1);
      event.preventDefault();
    }
    if (event.key === 'Enter' || event.key === ' ') {
      const addr = cellToAddress(cursor.x, cursor.y);
      memory[addr] = memory[addr] === 0 ? 255 : 0;
      event.preventDefault();
    }
    cursorVisible = true;
  }

  canvas.addEventListener('pointerdown', (event) => {
    canvas.focus();
    handleCanvasPointer(event.clientX, event.clientY);
  });
  canvas.addEventListener('keydown', onKeyDown);

  btnSafe.addEventListener('click', () => handleAction('safe'));
  btnOverflow.addEventListener('click', () => handleAction('overflow'));
  btnFree.addEventListener('click', () => handleAction('free'));
  btnUaf.addEventListener('click', () => handleAction('uaf'));
  btnDoubleFree.addEventListener('click', () => handleAction('doubleFree'));
  btnReset.addEventListener('click', () => handleAction('reset'));

  btnNext.addEventListener('click', () => applyStoryStep(storyIndex + 1));
  btnBack.addEventListener('click', () => applyStoryStep(storyIndex - 1));

  freePlayToggle.addEventListener('change', () => {
    freePlay = freePlayToggle.checked;
    updateActionAvailability();
  });

  advancedToggle.addEventListener('change', () => {
    advancedMode = advancedToggle.checked;
    applyStoryStep(storyIndex);
  });

  freePlayToggle.checked = true;
  updateActionAvailability();
  applyStoryStep(0);
  updateDangerMeter();
  animate();
};

if (root) {
  initMemoryPlayground(root);
}
